import React, { Component, createRef, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as actions from '../../features/vibe-coding/redux/actions';
import {
  VIBE_DEFAULT_BLUEPRINT,
  VIBE_DEFAULT_MODEL_ID,
  getDefaultThinkingForModel,
  resolveThinkingForModel,
} from '../../features/vibe-coding/redux/constants';

import ChatPanel from './components/ChatPanel';
import WorkspacePanel from './components/WorkspacePanel';
import CommonPageComponent from '../../common/ui/CommonPageComponent';
import showAlert from '../../common/utils/alert';
import {
  getBuildLogs,
  getErrorMessage,
  getInitialFilePath,
  getShortChatError,
  isPngFile,
} from './vibeCodingUtils';
import {
  canResumeApprovedPlan,
  isAwaitingPlanApproval as isPlanAwaitingApproval,
} from './utils/planUtils';
import PreviewPanel from './components/PreviewPanel';
import AppLayout from './components/AppLayout';
import MoreWorkspace from './components/more/MoreWorkspace';
import LogsPanel from './components/LogsPanel';
import InitialPromptPage from './InitialPromptPage';
import { WorkspaceChromeContext } from '../home/shell/WorkspaceChromeContext';
import ExplorerInputModal from './components/ExplorerInputModal';
import { DEFAULT_MORE_SECTION } from './components/more/moreSections';

const WORKSPACE_REFRESH_DEBOUNCE_MS = 100;

export class DefaultPage extends Component {
  static propTypes = {
    actions: PropTypes.object.isRequired,
    history: PropTypes.object,
    location: PropTypes.object,
    match: PropTypes.object.isRequired,
    vibe: PropTypes.object.isRequired,
  };

  static contextType = WorkspaceChromeContext;

  state = {
    prompt: '',
    selectedModel: VIBE_DEFAULT_MODEL_ID,
    selectedThinking: getDefaultThinkingForModel(VIBE_DEFAULT_MODEL_ID),
    selectedPath: '',
    openTabs: [],
    editorValue: '',
    imagePreviewUrl: '',
    baseHash: null,
    dirty: false,
    remoteChanged: false,
    workspaceMode: 'code',
    previousWorkspaceMode: 'code',
    moreSection: DEFAULT_MORE_SECTION,
    moreMounted: false,
    explorerOpen: true,
    explorerWidth: 260,
    chatOpen: typeof window !== 'undefined' ? window.innerWidth > 768 : true,
    chatWidth: 380,
    aiPinned: false,

    previewStatus: '',
    previewError: '',
    previewLogs: [],

    explorerDialog: null,
    initialPromptHandled: false,
  };

  fileRequestSequence = 0;
  workspaceRefreshTimer = null;
  messagesContainerRef = createRef();
  historyPageSize = 12;
  previousScrollHeight = null;

  async componentDidMount() {
    this.publishChrome();
    const routeData = this.getRouteData();
    const { projectId, version, conversationId } = routeData;
    const { actions, history, location } = this.props;

    if (!conversationId) {
      await this.startNewChat();
      return;
    }

    const initialPrompt = location && location.state && location.state.initialPrompt;
    if (initialPrompt && !this.state.initialPromptHandled) {
      this.setState({ initialPromptHandled: true });
      if (history && history.replace) {
        history.replace({ pathname: location.pathname, state: {} });
      }
      if (projectId && version) {
        actions.generateVibeResponseStream({
          projectId,
          version,
          data: {
            prompt: initialPrompt.trim(),
            conversationId,
            blueprint: VIBE_DEFAULT_BLUEPRINT,
            isInitialRequest: true,
            ...this.buildModelPayload(),
          },
        });
      }
    }

    if (projectId && version) {
      actions.getConversationHistory({
        projectId,
        version,
        conversationId,
        limit: this.historyPageSize,
      });
      actions.getVibeRunState({ projectId, version, conversationId });
      this.loadWorkspace(conversationId);
    }
    this.publishChrome();
  }

  componentWillUnmount() {
    this.clearWorkspaceRefreshTimer();
    const props = this.props;
    if (props.vibe && props.vibe.generateVibeResponsePending) {
      props.actions.stopVibeGeneration({ silent: true });
    }
    if (this.context && this.context.setVibeChrome) {
      this.context.setVibeChrome(null);
    }
  }

  componentDidUpdate(previousProps) {
    const { actions, vibe } = this.props;

    const previousVibe = previousProps.vibe || {};
    const errorMessage =
      getErrorMessage(vibe.generateVibeResponseError, '') || getErrorMessage(vibe.planError, '');
    const previousErrorMessage =
      getErrorMessage(previousVibe.generateVibeResponseError, '') ||
      getErrorMessage(previousVibe.planError, '');
    if (errorMessage && errorMessage !== previousErrorMessage) {
      toast.error(getShortChatError(errorMessage));
    }

    const routeData = this.getRouteData();
    const { projectId, version, conversationId } = routeData;

    const previousMatch = previousProps.match || {};
    const previousParams = previousMatch.params || {};
    const previousRouteConversationId = previousParams.conversationId || '';
    const previousProjectId = previousParams.projectId || '';

    if (projectId && previousProjectId && previousProjectId !== projectId) {
      if (previousVibe.generateVibeResponsePending) {
        actions.stopVibeGeneration({ silent: true });
      }
    }

    if (conversationId && previousRouteConversationId !== conversationId) {
      if (previousRouteConversationId && previousRouteConversationId !== conversationId) {
        if (previousVibe.generateVibeResponsePending) {
          actions.stopVibeGeneration({ silent: true });
        }
      }
      actions.getConversationHistory({
        projectId,
        version,
        conversationId,
        limit: this.historyPageSize,
      });
      actions.getVibeRunState({ projectId, version, conversationId });
      this.loadWorkspace(conversationId);
    } else if (!conversationId && previousRouteConversationId) {
      if (previousVibe.generateVibeResponsePending) {
        actions.stopVibeGeneration({ silent: true });
      }
      this.startNewChat();
    }

    const runEvents = Array.isArray(vibe.runEvents) ? vibe.runEvents : [];
    const previousRunEvents = Array.isArray(previousVibe.runEvents) ? previousVibe.runEvents : [];
    if (runEvents.length > previousRunEvents.length)
      this.handleLatestRunEvent(runEvents, projectId, version);

    const wasRunning = Boolean(previousVibe.generateVibeResponsePending);
    const isRunning = Boolean(vibe.generateVibeResponsePending);
    if (wasRunning && !isRunning) {
      this.scheduleWorkspaceRefresh(projectId, version);
    }

    const messages = Array.isArray(vibe.messages) ? vibe.messages : [];
    const previousMessages = Array.isArray(previousVibe.messages) ? previousVibe.messages : [];
    const streamingMessage = vibe.streamingMessage || '';
    const previousStreamingMessage = previousVibe.streamingMessage || '';
    const currentPhase = vibe.currentPhase;
    const previousPhase = previousVibe.currentPhase;
    if (
      messages.length > previousMessages.length ||
      streamingMessage !== previousStreamingMessage ||
      (isRunning && (runEvents.length > previousRunEvents.length || currentPhase !== previousPhase))
    ) {
      const messagesRef = this.messagesContainerRef;
      const container = messagesRef && messagesRef.current;
      if (
        container &&
        !vibe.getConversationHistoryMorePending &&
        !previousVibe.getConversationHistoryMorePending
      ) {
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      }
    }
    if (
      previousVibe.getConversationHistoryMorePending &&
      !vibe.getConversationHistoryMorePending &&
      this.previousScrollHeight !== null
    ) {
      const messagesRef = this.messagesContainerRef;
      const container = messagesRef && messagesRef.current;
      if (container) {
        container.scrollTop += container.scrollHeight - this.previousScrollHeight;
      }
      this.previousScrollHeight = null;
    }
    this.publishChrome();
  }

  setWorkspaceMode = (workspaceMode) => {
    this.setState((prev) => {
      const enteringMore = workspaceMode === 'more' && prev.workspaceMode !== 'more';
      const previousWorkspaceMode =
        enteringMore && (prev.workspaceMode === 'preview' || prev.workspaceMode === 'code')
          ? prev.workspaceMode
          : prev.previousWorkspaceMode;
      return {
        workspaceMode,
        previousWorkspaceMode,
        moreMounted: prev.moreMounted || workspaceMode === 'more',
      };
    });
  };

  loadOlderConversationHistory = () => {
    const { projectId, version, conversationId } = this.getRouteData();
    const { actions, vibe } = this.props;
    if (
      !projectId ||
      !version ||
      !conversationId ||
      !vibe.conversationHistoryHasMore ||
      vibe.getConversationHistoryMorePending ||
      !vibe.conversationHistoryBefore
    ) {
      return;
    }

    const container = this.messagesContainerRef && this.messagesContainerRef.current;
    this.previousScrollHeight = container ? container.scrollHeight : null;

    actions.getConversationHistory({
      projectId,
      version,
      conversationId,
      limit: this.historyPageSize,
      before: vibe.conversationHistoryBefore,
      append: true,
    });
  };

  setMoreSection = (moreSection) => {
    this.setState({ moreSection });
  };

  closeMore = () => {
    const previous = this.state.previousWorkspaceMode;
    const next = previous === 'preview' || previous === 'code' ? previous : 'code';
    this.setWorkspaceMode(next);
  };

  toggleExplorer = () => {
    this.setState((prev) => ({ explorerOpen: !prev.explorerOpen }));
  };

  toggleChat = () => {
    this.setState((prev) => ({ chatOpen: !prev.chatOpen }));
  };

  setChatWidth = (width) => {
    const viewportCap = typeof window !== 'undefined' ? Math.floor(window.innerWidth / 2) : 640;
    const max = Math.min(640, viewportCap);
    const min = Math.min(280, max);
    const next = Math.min(max, Math.max(min, Math.round(width)));
    this.setState((prev) => (prev.chatWidth === next ? prev : { chatWidth: next }));
  };

  setExplorerWidth = (width) => {
    const viewportCap = typeof window !== 'undefined' ? Math.floor(window.innerWidth / 3) : 480;
    const max = Math.min(480, viewportCap);
    const min = Math.min(160, max);
    const next = Math.min(max, Math.max(min, Math.round(width)));
    this.setState((prev) => (prev.explorerWidth === next ? prev : { explorerWidth: next }));
  };

  toggleAiPin = () => {
    this.setState((prev) => ({ aiPinned: !prev.aiPinned }));
  };

  publishChrome = () => {
    if (!this.context || !this.context.setVibeChrome) return;
    const { workspaceMode, dirty, explorerOpen, chatOpen } = this.state;
    const isSaving = Boolean(this.props.vibe && this.props.vibe.workspaceSavePending);
    const snapshot = `${workspaceMode}|${dirty}|${isSaving}|${chatOpen}|${explorerOpen}`;
    if (this.chromeSnapshot === snapshot) return;
    this.chromeSnapshot = snapshot;
    this.context.setVibeChrome({
      workspaceMode,
      isDirty: dirty,
      isSaving,
      chatOpen,
      onSave: this.handleSaveFile,
      onModeChange: this.setWorkspaceMode,
      onToggleChat: this.toggleChat,
    });
  };

  startNewChat = async () => {
    const { projectId, version } = this.getRouteData();
    const { actions, history } = this.props;
    if (!projectId || !version || !history) return;

    try {
      const defaultConversation = await actions.getDefaultConversation({ projectId, version });
      const defaultMessage =
        Array.isArray(defaultConversation) && defaultConversation.length > 0
          ? defaultConversation[0]
          : null;

      if (!defaultMessage || !defaultMessage.uuid) {
        this.setState({ showInitialPrompt: true });
        return;
      }

      history.replace(`/2.0/vibe-coding/${version}/v/${projectId}/chat/${defaultMessage.uuid}`);
    } catch (error) {
      toast.error(
        getShortChatError(getErrorMessage(error, 'Unable to load the default conversation.')),
      );
    }
  };

  getRouteData = () => {
    const props = this.props;
    const match = props.match;
    const params = match && match.params ? match.params : {};
    const location = props.location;
    const search = location && location.search ? location.search : '';
    const searchParams = new URLSearchParams(search);
    const queryConversationId = searchParams.get('conversationId');
    const routeConversationId = params.conversationId;
    return {
      projectId: params.projectId || '',
      version: params.version || '',
      conversationId: routeConversationId || queryConversationId || '',
    };
  };

  getActiveConversationId = () => {
    const routeData = this.getRouteData();
    const routeConversationId = routeData.conversationId;
    if (routeConversationId) return routeConversationId;

    const props = this.props;
    const vibe = props.vibe;
    return vibe.conversationId || '';
  };

  clearWorkspaceRefreshTimer = () => {
    if (this.workspaceRefreshTimer) {
      clearTimeout(this.workspaceRefreshTimer);
      this.workspaceRefreshTimer = null;
    }
  };

  scheduleWorkspaceRefresh = (projectId, version) => {
    this.clearWorkspaceRefreshTimer();
    this.workspaceRefreshTimer = setTimeout(() => {
      this.workspaceRefreshTimer = null;
      this.refreshWorkspaceListing(projectId, version);
    }, WORKSPACE_REFRESH_DEBOUNCE_MS);
  };

  refreshWorkspaceListing = async (projectId, version) => {
    if (!projectId || !version) return;
    const conversationId = this.getActiveConversationId();
    const { actions } = this.props;

    try {
      await actions.getWorkspace({ projectId, version, conversationId });
    } catch (error) {
      return error;
    }
  };

  handleLatestRunEvent = (runEvents, projectId, version) => {
    const event = runEvents[runEvents.length - 1];
    const type = event && event.type;
    if (type !== 'file_changed') return;

    this.scheduleWorkspaceRefresh(projectId, version);

    const path = event.path;
    const state = this.state;
    if (path && path === state.selectedPath) {
      if (state.dirty) {
        this.setState({ remoteChanged: true });
      } else {
        this.loadFile(path);
      }
    }
  };

  loadWorkspace = async (conversationId) => {
    const routeData = this.getRouteData();
    const { projectId, version } = routeData;
    if (!projectId || !version) return;

    const { actions } = this.props;

    try {
      const workspace = await actions.getWorkspace({ projectId, version, conversationId });
      const initialPath = getInitialFilePath(workspace);
      if (initialPath) this.selectFile(initialPath);
    } catch (error) {
      return error;
    }
  };

  loadFile = async (path) => {
    if (!path) return;
    const conversationId = this.getActiveConversationId();

    this.fileRequestSequence += 1;
    const requestSequence = this.fileRequestSequence;
    const routeData = this.getRouteData();
    const { projectId, version } = routeData;
    if (!projectId || !version) return;

    const { actions } = this.props;
    try {
      const file = await actions.getWorkspaceFile({ projectId, version, conversationId, path });
      if (requestSequence !== this.fileRequestSequence) return;

      const isPng = isPngFile(path);
      const content = file && typeof file.content === 'string' ? file.content : '';
      const imagePreviewUrl =
        isPng && file && typeof file.imageData === 'string'
          ? `data:${file.contentType || 'image/png'};base64,${file.imageData}`
          : '';
      const hash = file && file.hash ? file.hash : null;
      this.setState({
        selectedPath: path,
        editorValue: isPng ? '' : content,
        imagePreviewUrl,
        baseHash: hash,
        dirty: false,
        remoteChanged: false,
      });
    } catch (error) {
      return error;
    }
  };

  selectFile = (path) => {
    if (!path) return;
    const state = this.state;
    if (state.dirty) {
      showAlert(
        'Discard unsaved edits in current file?',
        () => this.openFile(path),
        'Discard edits',
      );
      return;
    }
    this.openFile(path);
  };

  openFile = (path) => {
    this.setState((previousState) => {
      const previousTabs = previousState.openTabs;
      const openTabs = previousTabs.includes(path) ? previousTabs : [...previousTabs, path];
      return { openTabs };
    });
    this.loadFile(path);
  };

  closeTab = (path, event) => {
    if (event) event.stopPropagation();
    const state = this.state;
    if (state.dirty && path === state.selectedPath) {
      showAlert(
        'Discard unsaved edits in current file?',
        () => this.closeFileTab(path),
        'Discard edits',
      );
      return;
    }
    this.closeFileTab(path);
  };

  closeFileTab = (path) => {
    this.setState(
      (previousState) => {
        const previousTabs = previousState.openTabs;
        const openTabs = previousTabs.filter((tabPath) => tabPath !== path);
        const isSelected = previousState.selectedPath === path;
        const selectedPath = isSelected
          ? openTabs[openTabs.length - 1] || ''
          : previousState.selectedPath;
        return { openTabs, selectedPath, editorValue: isSelected ? '' : previousState.editorValue };
      },
      () => {
        const state = this.state;
        if (state.selectedPath) this.loadFile(state.selectedPath);
      },
    );
  };

  handleEditorChange = (value) => this.setState({ editorValue: value || '', dirty: true });

  handleSaveFile = async () => {
    const state = this.state;
    const selectedPath = state.selectedPath;
    if (!selectedPath || isPngFile(selectedPath)) return;

    const conversationId = this.getActiveConversationId();
    const routeData = this.getRouteData();
    const { projectId, version } = routeData;
    if (!projectId || !version) return;

    const { actions } = this.props;

    try {
      const result = await actions.saveWorkspaceFile({
        projectId,
        version,
        conversationId,
        path: selectedPath,
        content: state.editorValue,
        baseHash: state.baseHash,
      });
      const hash = result && result.hash ? result.hash : state.baseHash;
      this.setState({ baseHash: hash, dirty: false, remoteChanged: false });
    } catch (error) {
      return error;
    }
  };

  handlePromptChange = (event) => {
    const target = event.target;
    const value = target && typeof target.value === 'string' ? target.value : '';
    this.setState({ prompt: value });
  };

  handleModelChange = (modelId) => {
    if (!modelId) return;
    this.setState((prev) => ({
      selectedModel: modelId,
      selectedThinking: resolveThinkingForModel(modelId, prev.selectedThinking),
    }));
  };

  handleThinkingChange = (thinkingId) => {
    if (!thinkingId) return;
    this.setState({ selectedThinking: thinkingId });
  };

  buildModelPayload = () => ({
    model: this.state.selectedModel,
    thinking: this.state.selectedThinking,
  });

  handlePromptKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (this.isAwaitingPlanApproval()) return;
      this.handleSendPrompt();
    }
  };

  isAwaitingPlanApproval = () => isPlanAwaitingApproval(this.props.vibe.planProposal);

  canResumeApprovedPlan = () =>
    canResumeApprovedPlan(this.props.vibe.planProposal, this.props.vibe.milestones);

  handleSendPrompt = async () => {
    const state = this.state;
    const promptValue = state.prompt;
    const prompt = promptValue.trim();
    const routeData = this.getRouteData();
    const { projectId, version } = routeData;
    const props = this.props;
    const vibe = props.vibe;
    const { actions } = props;
    const conversationId = this.getActiveConversationId();
    const isPending = Boolean(vibe.generateVibeResponsePending);
    const isPlanBusy = Boolean(vibe.planPending);
    const isAwaitingPlanApproval = this.isAwaitingPlanApproval();
    if (!prompt || !projectId || !version || isPending || isPlanBusy || isAwaitingPlanApproval)
      return;

    this.setState({ prompt: '' });

    const result = await actions.generateVibeResponseStream({
      projectId,
      version,
      data: {
        prompt,
        conversationId,
        currentDocumentPath: state.selectedPath,
        blueprint: VIBE_DEFAULT_BLUEPRINT,
        ...this.buildModelPayload(),
      },
    });

    if (!result || result.ok === false) return;
    this.scheduleWorkspaceRefresh(projectId, version);
  };

  handleApprovePlan = async () => {
    const { projectId, version } = this.getRouteData();
    const proposal = this.props.vibe.planProposal;
    if (!proposal || !proposal.planId || !projectId || !version) return;
    await this.props.actions.generateVibeResponseStream({
      projectId,
      version,
      data: {
        conversationId: proposal.conversationId || this.getActiveConversationId(),
        planAction: 'approve',
        planId: proposal.planId,
        planVersion: proposal.planVersion,
        blueprint: VIBE_DEFAULT_BLUEPRINT,
        ...this.buildModelPayload(),
      },
    });
  };

  handleRequestPlanChanges = async () => {
    const { projectId, version } = this.getRouteData();
    const proposal = this.props.vibe.planProposal;
    const prompt = this.state.prompt.trim();
    if (!prompt) {
      showAlert('Please enter your requested changes in the prompt composer first.', 'warning');
      return;
    }
    this.setState({ prompt: '' });
    const planId = proposal && proposal.planId;
    const planVersion = proposal && proposal.planVersion;
    const conversationId = (proposal && proposal.conversationId) || this.getActiveConversationId();
    await this.props.actions.generateVibeResponseStream({
      projectId,
      version,
      data: {
        conversationId,
        planAction: 'revise',
        planId,
        planVersion,
        prompt,
        blueprint: VIBE_DEFAULT_BLUEPRINT,
        ...this.buildModelPayload(),
      },
    });
  };

  handleClosePreview = () => {
    this.setState({ workspaceMode: 'code', previewError: '', previewStatus: 'idle' });
  };

  handleStopGeneration = () => {
    const { actions } = this.props;
    actions.stopVibeGeneration();
  };

  handleContinueBuilding = async () => {
    const routeData = this.getRouteData();
    const { projectId, version } = routeData;
    const { actions, vibe } = this.props;

    const conversationId = this.getActiveConversationId();
    const isPending = Boolean(vibe.generateVibeResponsePending);
    const isPlanBusy = Boolean(vibe.planPending);

    if (!projectId || !version || isPending || isPlanBusy) return;

    const result = await actions.generateVibeResponseStream({
      projectId,
      version,
      data: {
        prompt: 'continue',
        conversationId,
        currentDocumentPath: this.state.selectedPath,
        blueprint: VIBE_DEFAULT_BLUEPRINT,
        ...this.buildModelPayload(),
      },
    });

    if (!result || result.ok === false) return;
    this.scheduleWorkspaceRefresh(projectId, version);
  };

  setPreviewStatus = (status) => this.setState({ previewStatus: status });
  setPreviewError = (error) =>
    this.setState({
      previewError: error || '',
      previewStatus: error ? 'error' : this.state.previewStatus,
    });

  openExplorerDialog = (dialog) => this.setState({ explorerDialog: dialog });
  closeExplorerDialog = () => this.setState({ explorerDialog: null });

  handleCreateFile = ({ parentPath = '' } = {}) => {
    const base = parentPath ? `${parentPath}/` : '';
    this.openExplorerDialog({
      type: 'create-file',
      initialValue: `${base}untitled.js`,
      title: 'New File',
      confirmText: 'Create',
    });
  };

  handleCreateFolder = ({ parentPath = '' } = {}) => {
    const base = parentPath ? `${parentPath}/` : '';
    this.openExplorerDialog({
      type: 'create-folder',
      initialValue: `${base}new-folder`,
      title: 'New Folder',
      confirmText: 'Create',
    });
  };

  handleRenameNode = ({ node } = {}) => {
    if (!node || !node.path) return;
    this.openExplorerDialog({
      type: 'rename',
      node,
      initialValue: node.path,
      title: 'Rename',
      confirmText: 'Rename',
    });
  };

  handleDeleteNode = ({ node } = {}) => {
    if (!node || !node.path) return;
    if (!window.confirm(`Delete ${node.path}?`)) return;

    const { projectId, version } = this.getRouteData();
    this.props.actions
      .deleteWorkspaceFile({
        projectId,
        version,
        path: node.path,
        type: node.type === 'folder' ? 'folder' : 'file',
      })
      .then(() => {
        if (this.state.selectedPath === node.path) {
          this.setState({ selectedPath: '', editorValue: '', dirty: false });
        }
      })
      .catch((err) => showAlert(err.message || 'Delete failed'));
  };

  handleExplorerDialogConfirm = (path) => {
    const dialog = this.state.explorerDialog;
    this.closeExplorerDialog();
    console.log('path', path);
    if (!dialog || !path) return;

    const { projectId, version } = this.getRouteData();
    const actions = this.props.actions;
    const relative = String(path).trim().replace(/^\/+/, '');
    if (dialog.type === 'create-file') {
      actions
        .createWorkspaceFile({
          projectId,
          version,
          path: relative,
          folderPath: relative,
          content: '',
        })
        .then(() => actions.getWorkspace({ projectId, version }))
        .then(() => this.selectFile(relative))
        .catch((err) => showAlert(err.message || 'Create file failed'));
      return;
    }
    if (dialog.type === 'create-folder') {
      actions
        .createWorkspaceFolder({
          projectId,
          version,
          path: relative,
          folderPath: relative,
        })
        .then(() => actions.getWorkspace({ projectId, version }))
        .catch((err) => showAlert(err.message || 'Create folder failed'));
      return;
    } else if (dialog.type === 'rename') {
      actions
        .renameWorkspaceFile({
          projectId,
          version,
          from: dialog.node.path,
          to: relative,
        })
        .then(() => actions.getWorkspace({ projectId, version }))
        .then(() => {
          if (dialog.node.type !== 'folder') {
            this.selectFile(relative);
          }
        })
        .catch((err) => showAlert(err.message || 'Rename failed'));
    }
  };

  render() {
      const { conversationId } = this.getRouteData();
    if (this.state.showInitialPrompt) {
      const { projectId, version } = this.getRouteData();
      return (
        <InitialPromptPage
          history={this.props.history}
          location={this.props.location}
          projectId={projectId}
          version={version}
        />
      );
    }

    const { vibe, match } = this.props;
    const { projectId, version } = match.params;
    const { messages: rawMessages, activeArtifact: rawArtifact } = vibe;
    const state = this.state;

    const messages = Array.isArray(rawMessages) ? rawMessages.filter(Boolean) : [];

    const rawArtifactFiles = rawArtifact && rawArtifact.files;

    const artifactFiles = Array.isArray(rawArtifactFiles) ? rawArtifactFiles.filter(Boolean) : [];
    const activeArtifact = rawArtifact ? { ...rawArtifact, files: artifactFiles } : null;

    const workspaceError = getErrorMessage(vibe.workspaceError, '');
    const workspaceSaveError = getErrorMessage(vibe.workspaceSaveError, '');
    const errorMessage =
      getErrorMessage(vibe.generateVibeResponseError, '') ||
      getErrorMessage(vibe.planError, '') ||
      workspaceError;
    const buildLogs = getBuildLogs(vibe.runEvents, errorMessage);
    const previewLogs = Array.isArray(state.previewLogs) ? state.previewLogs : [];
    const streamingMessage = typeof vibe.streamingMessage === 'string' ? vibe.streamingMessage : '';
    const isRunning = Boolean(vibe.generateVibeResponsePending);
    const isPlanBusy = Boolean(vibe.planPending);
    const isAwaitingPlanApproval = this.isAwaitingPlanApproval();
    const canResumePlan = this.canResumeApprovedPlan();

    const isHistoryLoading = Boolean(vibe.getConversationHistoryPending);
    const isFileLoading = Boolean(vibe.workspaceFilePending);
    const showConflict = Boolean(vibe.workspaceConflict || state.remoteChanged);

    const codeEditorProps = {
      editorValue: state.editorValue,
      imagePreviewUrl: state.imagePreviewUrl,
      isDirty: state.dirty,
      isFileLoading,
      onCloseTab: this.closeTab,
      onEditorChange: this.handleEditorChange,
      onReloadFile: () => this.loadFile(state.selectedPath),
      onSave: this.handleSaveFile,
      onSelectFile: this.selectFile,
      openTabs: state.openTabs,
      selectedPath: state.selectedPath,
      showConflict,
      saveErrorMessage: workspaceSaveError,
    };

    return (
      <CommonPageComponent
        parentClassName="vibe-coding-page"
        containerClassName="container-fluid p-0"
      >
        <div className="vibe-coding-workspace">
          <AppLayout
            vibe={vibe}
            selectedPath={state.selectedPath}
            onSelectFile={this.selectFile}
            workspaceMode={state.workspaceMode}
            explorerOpen={state.explorerOpen}
            explorerWidth={state.explorerWidth}
            chatOpen={state.chatOpen}
            chatWidth={state.chatWidth}
            aiPinned={state.aiPinned}
            onToggleExplorer={this.toggleExplorer}
            onToggleChat={this.toggleChat}
            onChatWidthChange={this.setChatWidth}
            onExplorerWidthChange={this.setExplorerWidth}
            onCreateFile={this.handleCreateFile}
            onCreateFolder={this.handleCreateFolder}
            onRename={this.handleRenameNode}
            onDelete={this.handleDeleteNode}
            terminal={<LogsPanel logs={[...buildLogs, ...previewLogs]} />}
            moreMounted={state.moreMounted}
            more={
              state.moreMounted ? (
                <MoreWorkspace
                  section={state.moreSection}
                  onSectionChange={this.setMoreSection}
                  onClose={this.closeMore}
                  projectId={projectId}
                  version={version}
                  conversationId={conversationId}
                />
              ) : null
            }
            chat={
              <ChatPanel
                activeArtifact={activeArtifact}
                currentPhase={vibe.currentPhase}
                canResumeApprovedPlan={canResumePlan}
                isAwaitingPlanApproval={isAwaitingPlanApproval}
                isHistoryLoading={isHistoryLoading}
                isHistoryLoadingMore={Boolean(vibe.getConversationHistoryMorePending)}
                hasMoreHistory={Boolean(vibe.conversationHistoryHasMore)}
                isPlanBusy={isPlanBusy}
                isResumable={Boolean(vibe.isResumable)}
                isRunning={isRunning}
                messages={messages}
                messagesRef={this.messagesContainerRef}
                milestones={vibe.milestones || []}
                onApprovePlan={this.handleApprovePlan}
                onContinueBuilding={this.handleContinueBuilding}
                onPromptChange={this.handlePromptChange}
                onPromptKeyDown={this.handlePromptKeyDown}
                onRequestPlanChanges={this.handleRequestPlanChanges}
                onSelectFile={this.selectFile}
                onSend={this.handleSendPrompt}
                onStop={this.handleStopGeneration}
                onLoadOlder={this.loadOlderConversationHistory}
                onTogglePin={this.toggleAiPin}
                onToggleChat={this.toggleChat}
                onModelChange={this.handleModelChange}
                onThinkingChange={this.handleThinkingChange}
                selectedModel={state.selectedModel}
                selectedThinking={state.selectedThinking}
                aiPinned={state.aiPinned}
                planProposal={vibe.planProposal}
                prompt={state.prompt}
                stoppedInfo={vibe.stoppedInfo}
                streamingMessage={streamingMessage}
              />
            }
            preview={
              <PreviewPanel
                open={state.workspaceMode === 'preview'}
                status={state.previewStatus}
                setStatus={(status) => this.setState({ previewStatus: status })}
                error={state.previewError}
                setError={(error) => this.setState({ previewError: error })}
                onLog={(log) =>
                  this.setState((previousState) => ({
                    previewLogs: [...previousState.previewLogs, log],
                  }))
                }
                onClose={this.handleClosePreview}
                actions={this.props.actions}
                projectId={projectId}
                version={version}
              />
            }
          >
            <ExplorerInputModal
              isOpen={Boolean(this.state.explorerDialog)}
              title={(this.state.explorerDialog && this.state.explorerDialog.title) || ''}
              initialValue={
                (this.state.explorerDialog && this.state.explorerDialog.initialValue) || ''
              }
              confirmText={
                (this.state.explorerDialog && this.state.explorerDialog.confirmText) || 'OK'
              }
              label="Path"
              onCancel={this.closeExplorerDialog}
              onConfirm={this.handleExplorerDialogConfirm}
            />
            <WorkspacePanel codeEditorProps={codeEditorProps} workspaceError={workspaceError} />
          </AppLayout>
        </div>
      </CommonPageComponent>
    );
  }
}

function mapStateToProps(state) {
  const vibe = state.vibeCoding;
  return { vibe };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ ...actions }, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(DefaultPage);
